import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { API_BASE_URL } from "../../utils/constants";
import { generateTimeOptions } from "../../utils/timeUtils";

const TIME_OPTIONS = generateTimeOptions();

/**
 * Modal for assigning the next hearing date to selected contested cases.
 * Completely independent of the 1st/2nd/3rd hearing assignment flow.
 */
const ContestAssignModal = ({ show, onHide, selectedCases, arbId, onSuccess }) => {
  const [hearingDate, setHearingDate] = useState("");       // raw date string from meeting
  const [hearingDateDisplay, setHearingDateDisplay] = useState(""); // human-readable
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [linkId, setLinkId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Zoom meeting selector sub-state ──────────────────────────────────────────
  const [showMeetings, setShowMeetings] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [selectedMeetingLabel, setSelectedMeetingLabel] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setHearingDate("");
      setHearingDateDisplay("");
      setTimeFrom("");
      setTimeTo("");
      setVideoLink("");
      setLinkId("");
      setPassword("");
      setError("");
      setSelectedMeetingLabel("");
    }
  }, [show]);

  const fetchMeetings = async () => {
    if (!arbId) return;
    setMeetingsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Meetings?Arb_id=${arbId}`);
      if (res.ok) {
        const result = await res.json();
        const list = Array.isArray(result) ? result : (result.data || []);
        // Only show upcoming meetings
        const today = new Date();
        const upcoming = list.filter(m => m.Date ? new Date(m.Date) >= today : true);
        setMeetings(upcoming);
      }
    } catch {
      // non-critical
    } finally {
      setMeetingsLoading(false);
    }
  };

  const handleOpenMeetings = () => {
    fetchMeetings();
    setShowMeetings(true);
  };

  const handleSelectMeeting = (meeting) => {
    setVideoLink(meeting.Link || meeting.Video_link || "");
    setLinkId(meeting.Link_id || meeting.linkId || "");
    setPassword(meeting.Password || meeting.password || "");

    // Extract date from the meeting and use it as the hearing date
    const rawDate = meeting.Date || meeting.date || "";
    if (rawDate) {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        // Format as MM/DD/YYYY for the backend (matches toDateOnly parser)
        const formatted = d.toLocaleDateString("en-US");
        setHearingDate(formatted);
        setHearingDateDisplay(d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }));
      }
    }

    const label = meeting.Link_id
      ? `Meeting ID: ${meeting.Link_id}`
      : meeting.Link || "Selected";
    setSelectedMeetingLabel(label);
    setShowMeetings(false);
  };

  const handleSubmit = async () => {
    if (!selectedMeetingLabel) { setError("Please select a Zoom meeting."); return; }
    if (!timeFrom || !timeTo) { setError("Please select hearing time."); return; }
    setError("");
    setLoading(true);
    try {
      const formatted = hearingDate;
      const res = await fetch(`${API_BASE_URL}/api/contest/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caseIds: selectedCases.map(c => c.caseId),
          hearingDate: formatted,
          timeFrom,
          timeTo,
          videoLink,
          linkId,
          password,
        }),
      });
      if (res.ok) {
        onSuccess();
        onHide();
      } else {
        const err = await res.json().catch(() => null);
        setError(err?.message || "Assignment failed.");
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Main Assign Modal ──────────────────────────────────────────────── */}
      <Modal show={show && !showMeetings} onHide={onHide} centered>
        <Modal.Header closeButton className="customModal">
          <Modal.Title className="mx-auto">Assign Next Hearing</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "1.5rem" }}>

          {/* Selected cases summary */}
          <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
            Assigning hearing for <strong>{selectedCases.length}</strong> contested case(s).
          </p>

          {/* Time From / To */}
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label fw-semibold">Time From <span className="text-danger">*</span></label>
              <select className="form-select" value={timeFrom} onChange={e => setTimeFrom(e.target.value)}>
                <option value="">-- Select --</option>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold">Time To <span className="text-danger">*</span></label>
              <select className="form-select" value={timeTo} onChange={e => setTimeTo(e.target.value)}>
                <option value="">-- Select --</option>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Zoom Meeting */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Zoom Meeting <span className="text-danger">*</span></label>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <button className="custBtn" type="button" onClick={handleOpenMeetings}>
                Select Meeting
              </button>
              {selectedMeetingLabel && (
                <small className="text-success">{selectedMeetingLabel}</small>
              )}
            </div>
            {hearingDateDisplay && (
              <div className="mt-2" style={{ fontSize: "0.85rem", color: "#198754", fontWeight: 600 }}>
                Next Hearing Date: {hearingDateDisplay}
              </div>
            )}
          </div>

          {/* Meeting Link — auto-filled from meeting selection */}
          <div className="mb-3">
            <label className="form-label">Meeting Link</label>
            <input className="form-control" value={videoLink} onChange={e => setVideoLink(e.target.value)} placeholder="https://zoom.us/j/..." />
          </div>

          {error && <p className="text-danger" style={{ fontSize: "0.85rem" }}>{error}</p>}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center gap-2">
          <Button className="custBtn" onClick={handleSubmit} disabled={loading}>
            {loading ? "Assigning…" : "Assign Hearing"}
          </Button>
          <Button className="custBtn" onClick={onHide} disabled={loading}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Zoom Meeting Selector Sub-Modal ───────────────────────────────── */}
      <Modal show={showMeetings} onHide={() => setShowMeetings(false)} size="lg" centered>
        <Modal.Header closeButton className="customModal">
          <Modal.Title className="mx-auto">Select Zoom Meeting</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {meetingsLoading ? (
            <p className="text-center text-muted">Loading meetings…</p>
          ) : meetings.length === 0 ? (
            <p className="text-center text-muted">No upcoming meetings found.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-sm table-hover text-center" style={{ fontSize: "0.82rem" }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Meeting ID</th>
                    <th>Link</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((m, i) => (
                    <tr key={i}>
                      <td>{m.Date ? new Date(m.Date).toLocaleDateString("en-IN") : "—"}</td>
                      <td>{m.Link_id || m.linkId || "—"}</td>
                      <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.Link || m.Video_link || "—"}
                      </td>
                      <td>
                        <button className="custBtn" onClick={() => handleSelectMeeting(m)}>Select</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button className="custBtn" onClick={() => setShowMeetings(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ContestAssignModal;
